import { Checkbox, Input } from '@billy/design-system'
import { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

import { useInvoiceComplianceMode } from '@features/invoices/hooks/useInvoiceComplianceMode'
import { shouldAllowVatNumberWhenExempt } from '@features/invoices/utils/shouldAllowVatNumberWhenExempt'
import { countryIdToVatNoPlaceholderTKey } from '@features/invoicingSettings/constants/countryIdToVatNoPlaceholderTKey'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { useResolveTaxAndRegistrationNoLabel } from '../../../../../../../../../../../../hooks/useResolveTaxAndRegistrationNoLabel'
import { useMissingCompanyDetailsForm } from '../../../../../../contexts/missingCompanyDetailsFormContext'

export const CompanyVatNumber = (): ReactElement => {
  const { t } = useTranslation()
  const {
    missingCompanyDetailsForm: {
      FormItem,
      formState: { errors },
      setValue,
      trigger,
      watch,
    },
  } = useMissingCompanyDetailsForm()
  const { organization } = useUserOrganization()
  const { vatNoLabel } = useResolveTaxAndRegistrationNoLabel()
  const { isFrenchComplianceMode } = useInvoiceComplianceMode()

  const countryId = organization?.countryId
  const allowVatNumberWhenExempt = shouldAllowVatNumberWhenExempt(countryId)
  const vatNoPlaceholder = countryId ? t(countryIdToVatNoPlaceholderTKey[countryId]) : ''
  const registrationNoError = errors?.registrationNo
  const isVatExempted = watch('isVatExempted')
  const isVatNumberDisabled = isVatExempted && !allowVatNumberWhenExempt

  return (
    <>
      <FormItem
        label={vatNoLabel}
        name="vatNo"
        render={({ field: { onChange, ...rest } }) => (
          <Input
            {...rest}
            disabled={isVatNumberDisabled}
            onChange={(value) => {
              onChange(value)
              // Non-French compliance mode requires at least one of vat/registration number.
              // Revalidate both to keep error state in sync when either field changes or is cleared.
              if ((!isFrenchComplianceMode && registrationNoError) || !value.target.value) {
                trigger(['registrationNo', 'vatNo'])
              }
            }}
            placeholder={vatNoPlaceholder}
            size="sm"
          />
        )}
      />
      <FormItem
        name="isVatExempted"
        render={({ field: { onChange, value, ...rest } }) => (
          <Checkbox
            {...rest}
            checked={value}
            onChange={(event) => {
              onChange(event)
              if (!allowVatNumberWhenExempt) {
                setValue('vatNo', '')
              }
              trigger(['registrationNo', 'vatNo'])
            }}
          >
            {t('settings.organization.organization_info.vat_exempted')}
          </Checkbox>
        )}
      />
    </>
  )
}
